// Machine-native file pair for the SmartDate X65: `<name>.image` (the label, same <Legend> XML as .itf)
// and `<name>.job` (a tiny UTF-16 pointer: <Job><ImageFileName>name.image</ImageFileName></Job>).
// Pure module: no DOM, runs in Node and the browser.
import { encodeItf, decodeItf } from './encoding.js';

const BAD_NAME_CHARS = /[\/:*?"<>|\u0000-\u001f]/g;
const EXT_RE = /\.(itf|image|job)$/i;

/** 'Poha 500g.itf' -> 'Poha 500g'. Path characters are removed; never returns an empty string. */
export function printerBaseName(filename) {
  let base = String(filename || '').replace(BAD_NAME_CHARS, '').trim();
  while (EXT_RE.test(base)) base = base.replace(EXT_RE, '').trim();
  return base || 'label';
}

/** The two file names the machine expects (the job must name the image exactly). */
export function printerFileNames(filename) {
  const base = printerBaseName(filename);
  return { base, image: `${base}.image`, job: `${base}.job` };
}

const escapeXml = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const unescapeXml = (s) => s.replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&apos;/g, "'").replace(/&amp;/g, '&');

/** UTF-16 LE + BOM, exactly the shape the machine writes (it adds one stray trailing code unit; we do not). */
export function buildJobBytes(imageName) {
  return encodeItf(`<Job><ImageFileName>${escapeXml(imageName)}</ImageFileName></Job>`);
}

/** Read the image name out of a .job file. Tolerates the machine's stray trailing character. Returns null if not a job. */
export function parseJobBytes(bytes) {
  let text;
  try {
    const u8 = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    // The machine's files can end with a lone extra byte pair; trim to an even length before decoding.
    text = decodeItf(u8.length % 2 ? u8.subarray(0, u8.length - 1) : u8).text;
  } catch {
    return null;
  }
  const m = /<Job>\s*<ImageFileName>([\s\S]*?)<\/ImageFileName>\s*<\/Job>/.exec(text);
  return m ? unescapeXml(m[1]) : null;
}

/** Logo/graphic files and font files a label refers to; these live on the printer, not in the .image. */
export function referencedAssets(xmlText) {
  const grab = (tag) => [...new Set([...xmlText.matchAll(new RegExp(`<${tag}>([^<]+)</${tag}>`, 'g'))].map((m) => m[1].trim()).filter(Boolean))];
  return { logos: grab('FileName'), fonts: grab('Filename') };
}

/** 'NGEDriver[SDX65iw.<guid>]/' -> instance id, or null for the generic 'NGEDriver[SDX65iw]/'. */
export function deviceInstance(uri) {
  const m = /^NGEDriver\[[^\].]+\.([^\]]+)\]\/?$/.exec(uri || '');
  return m ? m[1] : null;
}

/** Re-point the label at one specific machine. Only touches the DeviceURI attribute. */
export function withDeviceUri(xmlText, uri) {
  return xmlText.replace(/(<DeviceInfo\s+DeviceURI=)(["'])[^"']*\2/, (_, a, q) => `${a}${q}${uri}${q}`);
}
